import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { TextTooltip } from '@mdvip/shared/components/blocks/TextTooltip';
import { Alert, AlertDescription } from '@mdvip/shared/components/ui/alert';
import { Badge } from '@mdvip/shared/components/ui/badge';
import { Button } from '@mdvip/shared/components/ui/button';
import { Card } from '@mdvip/shared/components/ui/card';
import { Separator } from '@mdvip/shared/components/ui/separator';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';
import type { SavedPaymentAccount } from '@/hooks/payments/paymentApi';
import { useDefaultPaymentMethod } from '@/hooks/payments/useDefaultPaymentMethod';
import { getAccountDisplay } from '@/lib/payments/accountDisplay.format';
import { toAccountDisplayInput } from '@/lib/payments/defaultPaymentMethod.format';

interface DefaultPaymentMethodDisplayProps {
  /** Starts the one-time payment flow — the same page-owned modal the Upcoming Payments button opens. */
  onMakePayment: () => void;
  /** Sends the member to the Payment Accounts tab. */
  onManageMethods: () => void;
  className?: string;
}

/** Check-payment instructions. A plain tooltip rather than a link or dialog: the address is a
 *  two-line aside, not a destination. */
function PayingByMailTooltip() {
  const { t } = useTranslation('members');

  return (
    <TextTooltip
      icon="mail"
      label={t('payments.defaultPaymentMethod.payingByMail')}
      content={t('payments.defaultPaymentMethod.mailInstructions')}
    />
  );
}

/**
 * The default method itself: Default/Expired badge, the masked number, and a card's expiry. Runs the
 * saved method through `getAccountDisplay` so the masking and expiry format match every payment
 * table. Bank accounts carry no expiration and can never be expired, so both the Expired badge and
 * the expiry line simply fall out of the data.
 */
function MethodSummary({ method }: { method: SavedPaymentAccount }) {
  const { t } = useTranslation('members');
  const display = getAccountDisplay(toAccountDisplayInput(method));

  // `getAccountDisplay`'s generic arm covers the check/removed/unknown profiles a *transaction* can
  // carry. A saved method is always a card or a bank account, so the adapter never produces it.
  if (display.kind === 'generic') return null;

  const label = display.kind === 'card' ? `${display.network} ${display.maskedNumber}` : display.label;
  const expiry = display.kind === 'card' ? display.expiry : null;

  return (
    <>
      {method.isExpired ? (
        <Badge variant="destructive">
          <Icon name="alertTriangle" size="xs" />
          {t('payments.defaultPaymentMethod.expiredBadge')}
        </Badge>
      ) : (
        <Badge variant="success">
          <Icon name="circleCheck" size="xs" />
          {t('payments.defaultPaymentMethod.defaultBadge')}
        </Badge>
      )}
      <span className="text-sm font-medium text-foreground">{label}</span>
      {expiry && (
        <span className={cn('text-xs', method.isExpired ? 'text-destructive-foreground' : 'text-muted-foreground')}>
          {`${t('payments.defaultPaymentMethod.expPrefix')} ${expiry}`}
        </span>
      )}
    </>
  );
}

/**
 * The Payment Center's default-method action strip (the design system's `ActionStripPayment`): the
 * method the member's charges land on, beside the two actions that change or use it.
 *
 * The strip stays usable when the method can't be read — a load failure or a member with nothing on
 * file still gets both buttons, since neither depends on knowing the default.
 */
export function DefaultPaymentMethodDisplay({
  onMakePayment,
  onManageMethods,
  className,
}: DefaultPaymentMethodDisplayProps) {
  const { t } = useTranslation('members');
  const { data: method, isPending, isError } = useDefaultPaymentMethod();

  return (
    <Card className={cn('gap-0! py-0!', className)} data-slot="action-strip-payment">
      <div className="flex flex-col gap-3 px-6 pt-6 pb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2" aria-busy={isPending}>
          {isPending ? (
            <>
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-36" />
            </>
          ) : isError ? (
            <Alert variant="destructive" className="w-fit py-2">
              <Icon name="alertTriangle" weight="solid" />
              <AlertDescription>{t('payments.defaultPaymentMethod.errorMessage')}</AlertDescription>
            </Alert>
          ) : method ? (
            <MethodSummary method={method} />
          ) : (
            <span className="text-sm text-muted-foreground">{t('payments.defaultPaymentMethod.noDefault')}</span>
          )}
          <Separator orientation="vertical" className="h-5! bg-border-strong!" />
          <PayingByMailTooltip />
        </div>
        <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center">
          <Button variant="outline" onClick={onManageMethods} className="w-full sm:w-auto">
            {t('payments.defaultPaymentMethod.manageMethods')}
          </Button>
          <Button onClick={onMakePayment} className="w-full sm:w-auto">
            {t('payments.defaultPaymentMethod.makePayment')}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-1.5 border-t bg-muted px-6 py-3 text-xs text-muted-foreground">
        <Icon name="shieldCheck" weight="solid" className="text-success-foreground" />
        {t('payments.defaultPaymentMethod.secureNote')}
      </div>
    </Card>
  );
}
